import { useLayoutEffect } from 'react';

// Flip-up positioning for a `position: fixed` list portaled into document.body and
// anchored under `triggerRef`. Same decision as usePortalPickerRect: measure the card's
// natural height and open upward when it doesn't fit below but there is more room above.
// Applied imperatively (before paint) so the list never flashes in the wrong place.
export function useFlipFixedList(
  isOpen: boolean,
  triggerRef: React.RefObject<HTMLElement | null>,
  listRef: React.RefObject<HTMLElement | null>,
  gap = 4,
) {
  useLayoutEffect(() => {
    if (!isOpen) return;

    const place = () => {
      const trigger = triggerRef.current;
      const list = listRef.current;
      if (!trigger || !list) return;
      const box = trigger.getBoundingClientRect();
      const tabBar = document.querySelector('ion-tab-bar');
      const tabBarHeight = tabBar ? tabBar.getBoundingClientRect().height : 0;
      const spaceBelow = window.innerHeight - tabBarHeight - box.bottom - gap;
      const spaceAbove = box.top - gap;

      // Natural height with our own inline maxHeight cleared (restored in the same frame).
      list.style.maxHeight = '';
      const natural = list.getBoundingClientRect().height;
      const flipUp = natural > spaceBelow - 8 && spaceAbove > spaceBelow;
      const available = Math.max(96, (flipUp ? spaceAbove : spaceBelow) - 8);

      list.style.maxHeight = `${available}px`;
      // Flipped lists anchor by their bottom edge so the card always ends just above the
      // trigger, whatever height it actually renders at.
      if (flipUp) {
        list.style.top = 'auto';
        list.style.bottom = `${window.innerHeight - box.top + gap}px`;
      } else {
        list.style.bottom = '';
        list.style.top = `${box.bottom + gap}px`;
      }
    };

    place();
    document.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen, triggerRef, listRef, gap]);
}
